import React from 'react';

import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';

import { AssetContainer } from '@techstyle/react-assets';

import { desktop, mobile, grid, getColWidth } from '../../styles';
import { useTrackPromotionClick } from '../../utils/useTrackPromotionClick';
import { useTrackPromotionViewed } from '../../utils/useTrackPromotionViewed';
import { Component as AssetSlot } from '../AssetSlot';
import { Container as FullScreenCarousel } from '../FullScreenCarousel';
import { Component as ProductCell } from '../ProductCell';
import { Component as StyleContext } from '../StyleContext';
import { Component as TemplateBackgroundImageAsset } from '../TemplateBackgroundImageAsset';
import { Component as TemplateCaptionCarousel } from '../TemplateCaptionCarousel';
import { Component as TemplateFplsAsset } from '../TemplateFplsAsset';
import { Component as TemplateImageAsset } from '../TemplateImageAsset';

const Container = styled.div`
  background-image: ${props => (props.bgImg ? `url(${props.bgImg})` : 'none')};
  background-size: cover;
  background-repeat: no-repeat;

  ${desktop`
    max-width: 1024px;
    margin: 0 auto;
  `}

  ${mobile`
    padding-bottom: 0;
    padding-left: 32px;

    ${ProductCell.Container} {
      width: 50%;
      padding-right: 16px;
    }
  `}

  ${TemplateCaptionCarousel.Caption} {
    min-height: 130px;
  }

  /* Row of 2 cells with 4px gap */
  /* Note: IE11 needs the longhand property 'width' for calc() to work */
  ${grid.small.css`
     ${TemplateCaptionCarousel.Wrapper}{
        width: ${getColWidth('small', 2)};
        ${TemplateCaptionCarousel.Caption} {
          padding: ${props => `0 ${props.theme.spacing.tiny}px
            ${props.theme.spacing.moderate}px`};
          min-height: 20px;
        }
      }
    `};

  /* Row of 3 cells with 16px gap */
  ${grid.medium.css`
     ${TemplateCaptionCarousel.Wrapper}{
        width: ${getColWidth('medium', 2)};
        ${TemplateCaptionCarousel.Caption} {
          min-height: 20px;
          padding-bottom:  ${props => props.theme.spacing.moderate}px;
        }
      }
  `};

  /* Row of 4 cells with 3 x 20px gap */
  ${grid.large.css`
     ${TemplateCaptionCarousel.Wrapper}{
        width: ${getColWidth('xlarge', 2)};
      }
  `};

  /* Row of 4 cells with 3 x 20px gap */
  ${grid.xlarge.css`
     ${TemplateCaptionCarousel.Wrapper}{
        width: ${getColWidth('xlarge', 2)};
      }
    `};
`;

const responsiveTypography = css`
  font-size: 12px;

  ${desktop`
    font-size: 14px;
  `};

  @media screen and (min-width: ${props => props.theme.breakpoints.xlarge}px) {
    font-size: 16px;
  }
`;

const Row = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  height: 100%;
  width: 100%;
  margin-top: ${props => props.theme.spacing.small}px;

  &:first-child {
    margin-top: 0;
  }

  background-color: ${props => props.bgColor};
  ${responsiveTypography};
`;

export const HorizontalStyledFullScreenCarousel = styled(FullScreenCarousel)`
  margin-bottom: ${props => props.marginBottomDesktop}px;

  ${mobile`
    margin-bottom: ${props => props.marginBottomMobile}px;
  `};

  .slick-arrow {
    bottom: 27%;
  }

  ${grid.small.css`
     height: 215px;
     margin: 0 0 ${props => props.marginBottomMobile}px 0;

     .slick-dots {
       bottom: -19px;
       line-height: 40px;
     };

     ${FullScreenCarousel.StyledHTMLAsset} {
       font-size: 18px;
       font-weight: bold;
       line-height: 1.17;
     }
  `};

  /* Row of 3 cells with 16px gap */
  ${grid.medium.css`
     height: 441px;

     ${FullScreenCarousel.StyledHTMLAsset} {
       font-size: 24px;
       font-weight: bold;
       line-height: 1.5;
     }
  `};

  /* Row of 4 cells with 2 x 20px gap */
  ${grid.large.css`
     height: 588px;

     ${FullScreenCarousel.StyledHTMLAsset} {
       font-size: 36px;
       font-weight: bold;
       line-height: 1.25;
     }
  `};

  /* Row of 4 cells with 3 x 20px gap */
  ${grid.xlarge.css`
     height: 588px;

     ${FullScreenCarousel.StyledHTMLAsset} {
       font-size: 36px;
       font-weight: bold;
       line-height: 1.25;
     }
  `};
`;

const StyledTemplateFplsAsset = styled(TemplateFplsAsset)`
  margin: 0px;
  width: 100%;
`;

const breakpoints = [
  { ...grid.small, padding: 0 },
  { ...grid.medium, padding: 20 },
  {
    ...grid.large,
    columns: 3,
    columnWidth: grid.xlarge.columnWidth,
    padding: 20,
  },
  { ...grid.xlarge, columns: 3, padding: 20 },
];

const AssetSlotWithTracking = ({ ...props }) => {
  const dispatchTrackPromotionViewed = useTrackPromotionViewed();
  const dispatchTrackPromotionClicked = useTrackPromotionClick();

  const onAssetCtaClick = () => dispatchTrackPromotionClicked(props.asset);
  const onEnterViewport =
    props.asset.type === 'marketing'
      ? () => dispatchTrackPromotionViewed(props.asset)
      : null;

  return (
    <AssetSlot
      {...props}
      onAssetCtaClick={onAssetCtaClick}
      onEnterViewport={onEnterViewport}
    />
  );
};

AssetSlotWithTracking.propTypes = {
  asset: PropTypes.object,
};

class FeaturedTemplateBody extends React.Component {
  static propTypes = {
    className: PropTypes.string,
    containerName: PropTypes.string.isRequired,
    onAssetEnterViewport: PropTypes.func,
    onAssetClick: PropTypes.func,
    pSource: PropTypes.string,
  };

  static defaultProps = {};

  renderAsset(asset, assetSlots, shouldHidePrice, useAdditionalParams = false) {
    const {
      options: {
        customVars: { fpl, imagePosition, fplProductsCount, shouldHideFpl },
      },
    } = asset;
    if (fpl || shouldHideFpl) {
      return (
        <StyledTemplateFplsAsset
          asset={asset}
          pSource={this.props.pSource}
          assetSlots={assetSlots}
          additionalParams={
            useAdditionalParams
              ? {
                  includeOutOfStock: false,
                  sort: 'topconverters', // Top converters in 24 hours
                  minimumQuantity: 5, // At least 5 in stock
                  groupBy: 'label',
                  productTypeIds: 1, // No sets
                }
              : null
          }
          breakpoints={breakpoints}
          sizeLimit={fplProductsCount || 28}
          applyUserSizes={false}
          shouldHidePrice={shouldHidePrice}
        />
      );
    }

    const isBackground = imagePosition
      ? imagePosition.startsWith('background')
      : false;

    if (isBackground) {
      return <TemplateBackgroundImageAsset asset={asset} />;
    }

    return <TemplateImageAsset asset={asset} />;
  }

  render() {
    const { className, containerName, onAssetClick, onAssetEnterViewport } =
      this.props;

    return (
      <StyleContext context="bca">
        <Container className={className} shouldHidePrice>
          <AssetContainer name={containerName} multi>
            {bodyAssets => {
              if (!bodyAssets || !bodyAssets.data) {
                return null;
              }

              const rows = [];
              let fpl;
              let shouldHidePrice = false;
              let shouldUsePriority = false;

              bodyAssets.data.forEach(asset => {
                const {
                  carouselContainerName,
                  contentOnly,
                  fpl: definedFpl,
                  gridPosition: definedGridPosition,
                  isVertical,
                } = asset.options.customVars;

                if (contentOnly) {
                  shouldUsePriority = true;
                }

                const hasFpl = !!definedFpl;
                const isCarousel = !!carouselContainerName;
                const gridPosition =
                  (shouldUsePriority ? asset.priority : definedGridPosition) -
                  1;
                const marginBottomDesktop =
                  asset.options.customVars.marginBottomDesktop || 64;
                const marginBottomMobile =
                  asset.options.customVars.marginBottomMobile || 40;

                if (
                  !shouldHidePrice &&
                  asset.options.customVars.shouldHidePrice
                ) {
                  shouldHidePrice = true;
                }

                // Without a bigger refactor, FPL content pages require
                // the `fpl` variable. `contentOnly` creates an empty
                // `fpl` variable that hides the product listings.
                if (hasFpl || !!contentOnly) {
                  fpl = { asset, assetSlots: {} };
                  rows.push(fpl);
                } else if (gridPosition || gridPosition === 0) {
                  if (isCarousel) {
                    if (!isVertical) {
                      fpl.assetSlots[gridPosition] = (
                        <HorizontalStyledFullScreenCarousel
                          marginBottomDesktop={marginBottomDesktop}
                          marginBottomMobile={marginBottomMobile}
                          key={asset.id}
                          container={
                            asset.options.customVars.carouselContainerName
                          }
                          videoHeight="100%"
                          needMobileArrows
                          data-span="fullWidth"
                          onAssetClick={onAssetClick}
                          onAssetEnterViewport={onAssetEnterViewport}
                        />
                      );
                    } else {
                      const carouselAlign = ['left', 'right'].includes(
                        asset.options.customVars.carouselAlignment
                      )
                        ? asset.options.customVars.carouselAlignment
                        : 'left';

                      fpl.assetSlots[gridPosition] = (
                        <TemplateCaptionCarousel
                          key={asset.id}
                          containerName={
                            asset.options.customVars.carouselContainerName
                          }
                          data-span={2}
                          data-align={carouselAlign}
                          marginBottomDesktop={marginBottomDesktop}
                          marginBottomMobile={marginBottomMobile}
                          onAssetClick={onAssetClick}
                          onAssetEnterViewport={onAssetEnterViewport}
                        />
                      );
                    }
                  } else if (
                    fpl.asset.flags &&
                    fpl.asset.flags.has('checkavailability')
                  ) {
                    // FPL products controls the asset to show in the grid
                    const { isSoldOut } = this.state;

                    if (asset.flags.has('isavailable') && !isSoldOut) {
                      fpl.assetSlots[gridPosition] = (
                        <AssetSlotWithTracking asset={asset} key={asset.id} />
                      );
                    } else if (asset.flags.has('isunavailable') && isSoldOut) {
                      fpl.assetSlots[gridPosition] = (
                        <AssetSlotWithTracking asset={asset} key={asset.id} />
                      );
                    }
                  } else {
                    fpl.assetSlots[gridPosition] = (
                      <AssetSlotWithTracking asset={asset} key={asset.id} />
                    );
                  }
                }
              });

              return rows.map(row => {
                const { options } = row.asset;
                return (
                  <Row
                    bgColor={options.bgColor || 'transparent'}
                    key={`QuizAsset-${row.asset.id}`}
                  >
                    {this.renderAsset(
                      row.asset,
                      row.assetSlots,
                      shouldHidePrice
                    )}
                  </Row>
                );
              });
            }}
          </AssetContainer>
        </Container>
      </StyleContext>
    );
  }
}

export default FeaturedTemplateBody;
